import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonWrapper } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    desc = "desc",
    link = "link",
    label = "label",
    cssClass = "cssClass",
    openInNewWidow = "openInNewWidow",
    emailInfo = "emailInfo",
    backgroundColor = "backgroundColor",
    borderColor = "borderColor",
    textColor = "textColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.cssClass]: string;
        [fields.desc]: string;
        [fields.link]: LinkFieldValue;
        [fields.label]: string;
        [fields.emailInfo]: string;
        [fields.openInNewWidow]: boolean;
        [fields.backgroundColor]: string;
        [fields.borderColor]: string;
        [fields.textColor]: string;
    };
}

type Props = OwnProps;

export interface SurStaticInfoStyles {
    container?: InjectableCss;
    ctacontainer?: InjectableCss;
    title?: InjectableCss;
    descStyles?: InjectableCss;
    ctaLink?: LinkProps;
    emailInfo?: InjectableCss;
}
export const surStaticInfoStyles: SurStaticInfoStyles = {
    container: {
        css: css`
            padding: 60px 0px 60px 0px;
            max-width: 955px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            row-gap: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 20px;
                        row-gap: 20px;
                    `,
                    css`
                        padding: 20px;
                        row-gap: 20px;
                    `,
                    css`
                        padding: 20px;
                        row-gap: 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    title: {
        css: css`
            font-family: "Mulish";
            font-style: normal;
            font-weight: 600;
            font-size: 28px;
            line-height: 36px;
            text-align: center;
            color: ${getColor("text.main")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        /* font-weight: 400;
                        font-size: 16px;
                        line-height: 24px; */
                    `,
                    css`
                        /* font-weight: 400;
                        font-size: 16px;
                        line-height: 24px; */
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    descStyles: {
        css: css`
            font-family: "Mulish";
            font-style: normal;
            font-weight: 400;
            font-size: 15px;
            line-height: 24px;
            text-align: center;
            color: ${getColor("text.main")};
        `,
    },
    ctacontainer: {
        css: css`
            text-align: center;
            margin-top: 30px;
            padding: 10px 34px;
            background: ${getColor("primary")};
        `,
    },
};

const styles = surStaticInfoStyles;

const SurFooterInfoContent: React.FC<Props> = ({ fields, id }) => {
    const { url } = useGetLink(fields.link);

    const ctaLink = {
        css: css`
            outline: 0;
            background-color: ${fields.backgroundColor};
            border: 1px solid ${fields.borderColor};
            padding: 0px 25.5px;
            width: max-content;
            max-width: 100%;
            display: flex;
            margin: 0 auto;
            height: 40px;
            span {
                display: block;
                font-family: "Mulish";
                font-style: normal;
                font-weight: 700;
                font-size: 15px;
                line-height: 24px;
                text-align: center;
                color: ${fields.textColor} !important;
                font-style: normal !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        span {
                            font-size: 15px;
                            line-height: ${TYPOGRAPHY.lineHeight.lsmall};
                        }
                    `,
                    css`
                        /* font-weight: 400;
                        font-size: 16px;
                        line-height: 24px; */
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    };

    return (
        <StyledWrapper {...styles.container}>
            {fields.title && <StyledWrapper {...styles.title}>{parse(fields.title, parserOptions)}</StyledWrapper>}
            {fields.desc && <StyledWrapper {...styles.descStyles}>{parse(fields.desc, parserOptions)}</StyledWrapper>}

            {fields.emailInfo && (
                <StyledWrapper {...styles.emailInfo}>{parse(fields.emailInfo, parserOptions)}</StyledWrapper>
            )}

            {fields.label && fields.link.value ? (
                <Link
                    href={url}
                    target={fields.openInNewWidow ? `_blank` : `_self`}
                    {...ctaLink}
                    aria-label={fields.label || "GetStarted"}
                >
                    {fields.label}
                </Link>
            ) : null}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SurFooterInfoContent,
    definition: {
        group: "Common",
        displayName: "SurFooterInfoContent",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.desc,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.label,
                fieldType: "General",
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 0,
            },

            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.borderColor,
                displayName: "Border Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.textColor,
                displayName: "Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                fieldType: "Translatable",
                name: fields.emailInfo,
                editorTemplate: "RichTextField",
                displayName: "Email Info",
                defaultValue: "",
            },
            {
                name: fields.link,
                displayName: "Button Link",
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
            },
            {
                name: fields.openInNewWidow,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default widgetModule;
